import { produce } from 'immer'
import React from 'react'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { createStore } from 'redux'
import './types'

/** @typedef {import('./types').ConversionTableRow} ConversionTableRow */
/** @typedef {import('./types').Classification} Classification */
/** @typedef {import('./types').ConversionItem} ConversionItem */
/** @typedef {import('./types').ValidationError} ValidationError */

/**
 * @typedef {Object} ConversionTableState
 * @property {ConversionTableRow[]} tableList - 変換表一覧
 * @property {Classification[]} classifications - 区分一覧
 * @property {ConversionTableRow | null} selectedTable - 選択中の変換表
 * @property {ConversionItem[]} conversionItems - 変換項目
 * @property {ConversionItem[] | null} previewItems - プレビュー項目
 * @property {ValidationError[]} validationErrors - バリデーションエラー
 * @property {string} importFileName - インポートファイル名
 * @property {string[]} importSheetNames - インポートシート名一覧
 * @property {string} selectedSheetName - 選択中のシート名
 * @property {boolean} isLoading - 読み込み中フラグ（変換項目テーブル用）
 * @property {boolean} isApiLoading - API実行中フラグ（モーダル・削除・登録処理用）
 * @property {string} sidebarSearch - サイドバー検索値
 * @property {string | null} initError - 初期化エラー
 * @property {string | null} deleteError - 削除エラー
 * @property {string | null} importError - インポートエラー
 * @property {Record<string, boolean>} validationErrorByTableKey - 変換表キーごとの変換項目バリデーションエラー有無（左タブ強調用）
 * @property {boolean} previewBaselineHadValidationIssue - インポートプレビュー開始時点で当該変換表に検証エラーがあったか（プレビュー破棄時の左タブ復元用）
 */

/** アクションタイプ定数 */
export const ACTION_TYPES = {
  SET_TABLE_LIST: 'SET_TABLE_LIST',
  SET_CLASSIFICATIONS: 'SET_CLASSIFICATIONS',
  SET_SELECTED_TABLE: 'SET_SELECTED_TABLE',
  SET_CONVERSION_ITEMS: 'SET_CONVERSION_ITEMS',
  SET_PREVIEW_ITEMS: 'SET_PREVIEW_ITEMS',
  SET_VALIDATION_ERRORS: 'SET_VALIDATION_ERRORS',
  SET_IMPORT_FILE_NAME: 'SET_IMPORT_FILE_NAME',
  SET_IMPORT_SHEET_NAMES: 'SET_IMPORT_SHEET_NAMES',
  SET_SELECTED_SHEET_NAME: 'SET_SELECTED_SHEET_NAME',
  SET_IS_LOADING: 'SET_IS_LOADING',
  SET_IS_API_LOADING: 'SET_IS_API_LOADING',
  SET_SIDEBAR_SEARCH: 'SET_SIDEBAR_SEARCH',
  SET_INIT_ERROR: 'SET_INIT_ERROR',
  SET_DELETE_ERROR: 'SET_DELETE_ERROR',
  SET_IMPORT_ERROR: 'SET_IMPORT_ERROR',
  SET_TABLE_VALIDATION_ERROR_FLAG: 'SET_TABLE_VALIDATION_ERROR_FLAG',
  CLEAR_TABLE_VALIDATION_ERROR_FLAGS: 'CLEAR_TABLE_VALIDATION_ERROR_FLAGS',
  SET_PREVIEW_BASELINE_VALIDATION: 'SET_PREVIEW_BASELINE_VALIDATION',
  RESET_PREVIEW: 'RESET_PREVIEW',
  RESET_STATE: 'RESET_STATE'
}

/** @type {ConversionTableState} */
const initialState = {
  tableList: [],
  classifications: [],
  selectedTable: null,
  conversionItems: [],
  previewItems: null,
  validationErrors: [],
  importFileName: '',
  importSheetNames: [],
  selectedSheetName: '',
  isLoading: false,
  isApiLoading: false,
  sidebarSearch: '',
  initError: null,
  deleteError: null,
  importError: null,
  validationErrorByTableKey: {},
  previewBaselineHadValidationIssue: false
}

/**
 * @param {ConversionTableState} state
 * @param {{ type: string; payload?: any }} action
 * @returns {ConversionTableState}
 */
export const conversionTableReducer = (state = initialState, action) => {
  if (action.type === ACTION_TYPES.RESET_STATE) {
    return initialState
  }
  return produce(state, (draft) => {
    const { payload, type } = action

    switch (type) {
      case ACTION_TYPES.SET_TABLE_LIST:
        draft.tableList = payload || []
        return

      case ACTION_TYPES.SET_CLASSIFICATIONS:
        draft.classifications = payload || []
        return

      case ACTION_TYPES.SET_SELECTED_TABLE:
        draft.selectedTable = payload
        return

      case ACTION_TYPES.SET_CONVERSION_ITEMS:
        draft.conversionItems = payload || []
        return

      case ACTION_TYPES.SET_PREVIEW_ITEMS:
        draft.previewItems = payload
        return

      case ACTION_TYPES.SET_VALIDATION_ERRORS:
        draft.validationErrors = payload || []
        return

      case ACTION_TYPES.SET_IMPORT_FILE_NAME:
        draft.importFileName = payload || ''
        return

      case ACTION_TYPES.SET_IMPORT_SHEET_NAMES:
        draft.importSheetNames = payload || []
        return

      case ACTION_TYPES.SET_SELECTED_SHEET_NAME:
        draft.selectedSheetName = payload || ''
        return

      case ACTION_TYPES.SET_IS_LOADING:
        draft.isLoading = payload ?? false
        return

      case ACTION_TYPES.SET_IS_API_LOADING:
        draft.isApiLoading = payload ?? false
        return

      case ACTION_TYPES.SET_SIDEBAR_SEARCH:
        draft.sidebarSearch = payload || ''
        return

      case ACTION_TYPES.SET_INIT_ERROR:
        draft.initError = payload
        return

      case ACTION_TYPES.SET_DELETE_ERROR:
        draft.deleteError = payload
        return

      case ACTION_TYPES.SET_IMPORT_ERROR:
        draft.importError = payload
        return

      case ACTION_TYPES.SET_TABLE_VALIDATION_ERROR_FLAG: {
        const { tableKey, hasError } = payload || {}
        if (!tableKey || typeof tableKey !== 'string') return
        if (hasError) {
          draft.validationErrorByTableKey[tableKey] = true
        } else {
          delete draft.validationErrorByTableKey[tableKey]
        }
        return
      }

      case ACTION_TYPES.CLEAR_TABLE_VALIDATION_ERROR_FLAGS:
        draft.validationErrorByTableKey = {}
        return

      case ACTION_TYPES.SET_PREVIEW_BASELINE_VALIDATION:
        draft.previewBaselineHadValidationIssue = Boolean(payload)
        return

      case ACTION_TYPES.RESET_PREVIEW:
        draft.previewItems = null
        draft.validationErrors = []
        draft.importFileName = ''
        draft.selectedSheetName = ''
        draft.importSheetNames = []
        draft.importError = null
        draft.previewBaselineHadValidationIssue = false
        return

      default:
        return
    }
  })
}

/**
 * Dispatchを変更
 *
 * @returns {function(string, any): void}
 */
export const useConversionTableDispatch = () => {
  const _dispatch = useDispatch()
  return (type, payload) => _dispatch({ type, payload })
}

/** @type {import('react-redux').TypedUseSelectorHook<ConversionTableState>} */
export const useConversionTableSelector = useSelector

/**
 * @function ConversionTableProviderStore プロバイダー
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export const ConversionTableProviderStore = (props) => {
  const [store] = React.useState(() => createStore(conversionTableReducer))
  return <Provider store={store}>{props.children}</Provider>
}
